import React, { useState } from 'react';
import { X, CheckCircle2, AlertCircle, Star } from 'lucide-react';
import { submitCsatSurvey } from '../services/firestoreService';

// Categories mirror the printed CSat 2024 form, in order.
const CATEGORIES = [
  {
    title: 'PROFICIENCY',
    items: ['Engineering / Technical Value Additions', 'Financial Soundness'],
  },
  {
    title: 'ON TIME DELIVERY — OTD',
    items: ['Site Mobilization', 'Supply of Equipments', 'Execution of Works'],
  },
  {
    title: 'QUALITY — FIRST TIME RIGHT',
    items: ['Workmanship', 'Tools / Tackles / Machinery'],
  },
  {
    title: 'SERVICE — CUSTOMER CENTRIC',
    items: ['General Behavior / Response'],
  },
  {
    title: 'COMPLIANCE',
    items: [
      'Adherence to EHS Standards — 9001, 14001, 18001',
      '5S Housekeeping Standards Maintained at Site & Stores',
    ],
  },
];

// 10 → 1, descending (left = Excellent, right = Poor) — matches PDF visual.
const SCALE = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

// Three-band colour grading to mirror the PDF's Excellent / Average / Poor key.
function bandFor(n) {
  if (n >= 8) return 'excellent';
  if (n >= 4) return 'average';
  return 'poor';
}
const BAND_STYLES = {
  excellent: {
    selected: 'bg-[#1F8A4C] text-white border-[#1F8A4C]',
    idle: 'bg-[#E8F4EC] text-[#1F8A4C] border-[#1F8A4C]/30 hover:bg-[#1F8A4C]/15',
  },
  average: {
    selected: 'bg-[#E8A53A] text-white border-[#E8A53A]',
    idle: 'bg-[#FBF1DE] text-[#A06A12] border-[#E8A53A]/35 hover:bg-[#E8A53A]/20',
  },
  poor: {
    selected: 'bg-[#C0392B] text-white border-[#C0392B]',
    idle: 'bg-[#F8E1DD] text-[#9A2A1F] border-[#C0392B]/30 hover:bg-[#C0392B]/15',
  },
};

function RatingRow({ label, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-3 md:gap-5 py-3 border-b border-[var(--line)] last:border-b-0">
      <div className="text-[14px] md:text-[15px] text-[var(--ink)] leading-snug">{label}</div>
      <div className="flex flex-wrap gap-1.5 md:gap-2" data-testid={testid}>
        {SCALE.map(n => {
          const band = bandFor(n);
          const isSel = value === n;
          const style = isSel ? BAND_STYLES[band].selected : BAND_STYLES[band].idle;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`w-8 h-8 md:w-9 md:h-9 rounded-md border-2 font-display font-bold text-[13px] md:text-[14px] transition-all ${style} ${isSel ? 'shadow-md scale-105' : ''}`}
              data-testid={`${testid}-${n}`}
              aria-label={`Rate ${n}`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FI({ label, value, onChange, placeholder, type, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-semibold">{label}</span>
      <input
        type={type || 'text'}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="cf-input"
        placeholder={placeholder}
        data-testid={testid}
      />
    </label>
  );
}

export default function CSatSurveyModal({ open, onClose }) {
  // initialise every rating-able item with null
  const initialRatings = () => {
    const r = {};
    CATEGORIES.forEach(cat => cat.items.forEach(item => { r[item] = null; }));
    return r;
  };
  const [ratings, setRatings] = useState(initialRatings);
  const [overall, setOverall] = useState(null);
  const [respondent, setRespondent] = useState({ name: '', designation: '', organisation: '', date: '' });
  const [recommend, setRecommend] = useState({ name: '', contact: '', organisation: '' });
  const [improvement, setImprovement] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  const setRating = (item, n) => setRatings(r => ({ ...r, [item]: n }));

  const reset = () => {
    setRatings(initialRatings());
    setOverall(null);
    setRespondent({ name: '', designation: '', organisation: '', date: '' });
    setRecommend({ name: '', contact: '', organisation: '' });
    setImprovement('');
    setStatus(null);
    setErrMsg('');
  };

  const handleClose = () => { reset(); onClose(); };

  const validate = () => {
    if (!respondent.name.trim()) return 'Please enter your name.';
    if (!respondent.organisation.trim()) return 'Please enter your organisation.';
    if (overall === null) return 'Please rate your overall experience.';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setErrMsg(v); return; }
    setErrMsg('');
    setBusy(true);
    try {
      await submitCsatSurvey({
        ratings,
        overall,
        respondent,
        recommend,
        improvement,
      });
      setStatus('ok');
    } catch (e) {
      console.error(e);
      setStatus('err');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-4" data-testid="csat-survey-modal">
      <div className="absolute inset-0 bg-[var(--ink)]/70 backdrop-blur-sm" onClick={handleClose}/>
      <div className="relative bg-[var(--cream)] rounded-2xl md:rounded-3xl w-full max-w-[920px] max-h-[92vh] overflow-hidden shadow-2xl flex flex-col border border-[var(--line)]">
        {/* Header — mimics the PDF letterhead */}
        <div className="relative bg-white border-b-2 border-[var(--orange)] px-6 md:px-10 py-5 md:py-6">
          <button onClick={handleClose} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center hover:bg-[var(--cream)] z-10" data-testid="csat-modal-close">
            <X className="w-4 h-4"/>
          </button>
          <div className="flex items-center justify-between gap-6 pr-12">
            <img src="/logos/full.png" alt="Tricolite Energy Solutions" className="h-12 md:h-14 w-auto" data-testid="csat-modal-logo"/>
            <div className="text-right">
              <h2 className="font-display font-bold text-[18px] md:text-[26px] leading-tight">Customer Satisfaction Survey</h2>
              <p className="text-[12px] md:text-[13px] text-[var(--text-mid)] mt-1 italic">We value your candid feedback on our performance.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 md:px-10 py-6 space-y-7">
          {status === 'ok' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="w-16 h-16 text-[#1F8A4C] mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Thank you!</div>
              <p className="text-[var(--text-mid)] max-w-md">Your feedback has been recorded. The TES leadership team will review it personally — we&apos;re grateful for the time you&apos;ve taken.</p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <a href="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--orange)] hover:bg-[var(--orange-soft)] text-white font-medium transition-colors" data-testid="csat-success-explore-home">Explore Our Website</a>
                <a href="/our-work" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[var(--line)] text-[var(--ink)] hover:border-[var(--orange)] font-medium transition-colors" data-testid="csat-success-explore-work">See Our Work</a>
                <button onClick={handleClose} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[var(--text-mid)] hover:text-[var(--ink)] font-medium transition-colors" data-testid="csat-success-close">Close</button>
              </div>
            </div>
          ) : status === 'err' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Submission Failed</div>
              <p className="text-[var(--text-mid)]">Please try again, or email solutions@tricoliteenergy.in directly.</p>
              <button onClick={() => setStatus(null)} className="mt-6 px-6 py-3 rounded-full bg-[var(--ink)] text-white font-medium">Try Again</button>
            </div>
          ) : (
            <>
              {/* Rating scale legend */}
              <div className="rounded-xl border border-[var(--line)] bg-white p-4 md:p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-light)] font-semibold mb-3">Rating scale</div>
                <div className="grid grid-cols-3 gap-2 md:gap-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-[#1F8A4C] text-white font-display font-bold text-[12px] md:text-[13px] flex items-center justify-center">10</div>
                    <div>
                      <div className="text-[12px] md:text-[13px] font-semibold">Excellent</div>
                      <div className="text-[10px] md:text-[11px] text-[var(--text-light)]">10 — 8</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-[#E8A53A] text-white font-display font-bold text-[12px] md:text-[13px] flex items-center justify-center">5</div>
                    <div>
                      <div className="text-[12px] md:text-[13px] font-semibold">Average</div>
                      <div className="text-[10px] md:text-[11px] text-[var(--text-light)]">7 — 4</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-[#C0392B] text-white font-display font-bold text-[12px] md:text-[13px] flex items-center justify-center">1</div>
                    <div>
                      <div className="text-[12px] md:text-[13px] font-semibold">Poor</div>
                      <div className="text-[10px] md:text-[11px] text-[var(--text-light)]">3 — 1</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories */}
              {CATEGORIES.map((cat, ci) => (
                <section key={cat.title} className="rounded-xl border border-[var(--line)] bg-white">
                  <div className="px-4 md:px-6 py-3 bg-[var(--ink)] text-white rounded-t-xl">
                    <h3 className="font-display font-bold text-[13px] md:text-[14px] tracking-[0.18em]">{String(ci + 1).padStart(2, '0')}. {cat.title}</h3>
                  </div>
                  <div className="px-4 md:px-6 py-2 md:py-3">
                    {cat.items.map((item, ii) => (
                      <RatingRow
                        key={item}
                        label={item}
                        value={ratings[item]}
                        onChange={(n) => setRating(item, n)}
                        testid={`csat-rating-${ci}-${ii}`}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {/* Overall experience */}
              <section className="rounded-xl border-2 border-[var(--orange)] bg-white">
                <div className="px-4 md:px-6 py-3 bg-[var(--orange)] text-white rounded-t-[10px] flex items-center gap-2">
                  <Star className="w-4 h-4"/>
                  <h3 className="font-display font-bold text-[13px] md:text-[14px] tracking-[0.18em]">OVERALL EXPERIENCE</h3>
                </div>
                <div className="px-4 md:px-6 py-4">
                  <RatingRow
                    label="Your overall experience working with Tricolite Energy Solutions"
                    value={overall}
                    onChange={setOverall}
                    testid="csat-rating-overall"
                  />
                </div>
              </section>

              {/* Respondent details */}
              <section className="rounded-xl border border-[var(--line)] bg-white p-5 md:p-6">
                <h3 className="font-display font-bold text-[14px] tracking-[0.16em] mb-4 text-[var(--orange)]">RESPONDENT DETAILS</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FI label="Name *" value={respondent.name} onChange={v => setRespondent(r => ({ ...r, name: v }))} placeholder="Full name" testid="csat-resp-name"/>
                  <FI label="Designation" value={respondent.designation} onChange={v => setRespondent(r => ({ ...r, designation: v }))} placeholder="e.g. Project Manager" testid="csat-resp-designation"/>
                  <FI label="Organisation *" value={respondent.organisation} onChange={v => setRespondent(r => ({ ...r, organisation: v }))} placeholder="Company name" testid="csat-resp-org"/>
                  <FI label="Date" value={respondent.date} onChange={v => setRespondent(r => ({ ...r, date: v }))} type="date" testid="csat-resp-date"/>
                </div>
              </section>

              {/* Recommendation */}
              <section className="rounded-xl border border-[var(--line)] bg-white p-5 md:p-6">
                <h3 className="font-display font-bold text-[14px] tracking-[0.16em] mb-1 text-[var(--orange)]">RECOMMEND US</h3>
                <p className="text-[12px] md:text-[13px] text-[var(--text-mid)] mb-4">Anybody you would like us to be recommended to based on our performance?</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FI label="Name of Person" value={recommend.name} onChange={v => setRecommend(r => ({ ...r, name: v }))} placeholder="Contact name" testid="csat-rec-name"/>
                  <FI label="Contact Details" value={recommend.contact} onChange={v => setRecommend(r => ({ ...r, contact: v }))} placeholder="Phone / email" testid="csat-rec-contact"/>
                  <FI label="Organisation" value={recommend.organisation} onChange={v => setRecommend(r => ({ ...r, organisation: v }))} placeholder="Company" testid="csat-rec-org"/>
                </div>
              </section>

              {/* Improvement */}
              <section className="rounded-xl border border-[var(--line)] bg-white p-5 md:p-6">
                <h3 className="font-display font-bold text-[14px] tracking-[0.16em] mb-1 text-[var(--orange)]">FURTHER IMPROVEMENT</h3>
                <p className="text-[12px] md:text-[13px] text-[var(--text-mid)] mb-3">Any inputs on further improvement for us?</p>
                <textarea
                  value={improvement}
                  onChange={e => setImprovement(e.target.value)}
                  className="cf-textarea"
                  placeholder="Share your honest, candid feedback..."
                  rows={4}
                  data-testid="csat-improvement"
                />
              </section>

              {errMsg && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-[13px] flex items-start gap-2" data-testid="csat-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
                  <span>{errMsg}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!status && (
          <div className="flex items-center justify-between gap-3 px-5 md:px-10 py-4 md:py-5 border-t border-[var(--line)] bg-white">
            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[var(--text-light)] font-semibold hidden md:block">Affordable, Clean & Efficient Energy Solutions</div>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={handleClose} className="px-5 py-2.5 rounded-full border border-[var(--line)] text-[14px] font-medium hover:bg-[var(--cream)] transition-colors">Cancel</button>
              <button onClick={submit} disabled={busy} className="px-6 py-2.5 rounded-full bg-[var(--orange)] text-white text-[14px] font-medium hover:bg-[var(--orange-soft)] transition-colors disabled:opacity-60" data-testid="csat-submit-btn">
                {busy ? 'Submitting…' : 'Submit Survey'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
